import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';

import { CardHeader } from '@material-ui/core';

import { getCollectionTree } from '../../../modules/collections';

function findPathToCollection(nodes, id, path = []) {
    for (const n of nodes) {
        if (n.id === id) return [...path, n];
        if (n.subcollections?.length) {
            const hit = findPathToCollection(n.subcollections, id, [...path, n]);
            if (hit) return hit;
        }
    }
    return null;
}

function descendantHasSelection(node) {
    if (!node.subcollections?.length) return false;
    return node.subcollections.some((s) => s.selected || descendantHasSelection(s));
}

function ensureParentsSelectedForCheckedChildren(nodes) {
    nodes.forEach((node) => {
        if (node.subcollections?.length) {
            ensureParentsSelectedForCheckedChildren(node.subcollections);
            if (node.subcollections.some((s) => s.selected)) {
                node.selected = true;
            }
        }
    });
}

const useStyles = makeStyles({
    collectionImage: {
        width: '20px',
        height: '20px',
        objectFit: 'cover',
        borderRadius: '3px',
        marginRight: '10px',
    },
    collectionContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    }
});

const Collection = ({ collection, depth = 0 }) => {
    const classes = useStyles();
    const checkDefault = collection && collection.default ? true : false;
    const selected = collection.selected;
    const lockedBySubcollection = !checkDefault && descendantHasSelection(collection);
    return (
        <>
        <div className={classes.collectionContainer} style={{ paddingLeft: depth ? 24 : 0 }}>
            <FormControlLabel
                control={
                    <Checkbox
                        color="primary"
                        checked={checkDefault ? true : selected}
                        disabled={checkDefault || lockedBySubcollection}
                        onChange={collection.handleChange}
                        name="checkedA"
                    />
                }
                label={collection.name}
            />
            {collection.picture_url && <img className={classes.collectionImage} src={process.env.REACT_APP_IMAGES_API_URL + collection.picture_url} alt={collection.name} />}
        </div>
        {Array.isArray(collection.subcollections) &&
            collection.subcollections.map((subcollection) => (
                <Collection depth={depth + 1} key={subcollection.id} collection={subcollection} />
            ))}
        </>
    );
}

const ProductCollections = ({
    isTabletOrMobile,
    control,
    setValue,
    storeId,
    collectionTree,
    gettingCollectionTree,
    getCollectionTree,
    productData
}) => {
    const { t } = useTranslation();
    const [collectionTreeState, setCollectionTreeState] = useState([]);

    useEffect(() => {
        getCollectionTree(storeId);
    }, [getCollectionTree, storeId]);

    useEffect(() => {
        const handleCollectionChange = (collectionId, checked) => {
            setCollectionTreeState((tree) => {
                const path = findPathToCollection(tree, collectionId);
                if (!path || path.length === 0) return tree;

                const target = path[path.length - 1];
                if (!checked && descendantHasSelection(target)) {
                    return tree;
                }

                const ancestorIds = new Set(path.slice(0, -1).map((n) => n.id));

                const applySelection = (nodes) =>
                    nodes.map((node) => {
                        let nextSelected = node.selected;
                        if (node.id === collectionId) nextSelected = checked;
                        else if (checked && ancestorIds.has(node.id)) nextSelected = true;

                        return {
                            ...node,
                            selected: nextSelected,
                            subcollections: node.subcollections?.length
                                ? applySelection(node.subcollections)
                                : [],
                        };
                    });

                const newTree = applySelection(tree);
                const attachHandlers = (c) => {
                    c.handleChange = ({ target }) =>
                        handleCollectionChange(c.id, target.checked);
                    if (c.subcollections && c.subcollections.length > 0) {
                        c.subcollections.forEach(attachHandlers);
                    }
                };
                newTree.forEach(attachHandlers);
                return newTree;
            });
        };

        const cloneTree = (nodes) =>
            nodes.map((node) => ({
                ...node,
                subcollections:
                    node.subcollections && node.subcollections.length > 0
                        ? cloneTree(node.subcollections)
                        : [],
            }));

        const workingTree = cloneTree(collectionTree);

        const setCollectionCheckboxes = (collection) => {
            collection.selected = false;
            collection.handleChange = ({ target }) => handleCollectionChange(collection.id, target.checked);
            if (collection.subcollections?.length) {
                collection.subcollections.forEach((sub) => setCollectionCheckboxes(sub));
            }
            return collection;
        };
        workingTree.forEach((c) => setCollectionCheckboxes(c));

        if (productData) {
            const productCollectionIds = (productData.collections || []).map(
                (c) => c.collection_id ?? c.id
            );
            const setSelectedCollection = (collection) => {
                collection.selected = productCollectionIds.indexOf(collection.id) !== -1;
                if (collection.subcollections?.length) {
                    collection.subcollections.forEach((sub) => setSelectedCollection(sub));
                }
                return collection;
            };
            workingTree.forEach((c) => setSelectedCollection(c));
            ensureParentsSelectedForCheckedChildren(workingTree);
        }
        setCollectionTreeState(workingTree);
    }, [collectionTree, productData]);

    useEffect(() => {
        const idsArray = [];

        const getCheckedIds = (collection) => {
            if (collection.selected) idsArray.push(collection.id);
            if (collection.subcollections?.length) {
                collection.subcollections.forEach((sub) => getCheckedIds(sub));
            }
        };

        collectionTreeState.forEach((collection) => getCheckedIds(collection));
        setValue('collections', idsArray);
    }, [collectionTreeState, setValue]);


    return (
        <Card elevation={2} style={{ marginBottom: '21px' }}>
            {gettingCollectionTree && <LinearProgress />}
            <CardHeader titleTypographyProps={{ variant: 'h6' }} title={t('CollectionsTitle')} style={{ paddingTop: 10 }} />
            <CardContent >
                <Grid container spacing={isTabletOrMobile ? 0 : 3}>
                    <Grid item xs={12}>
                        {collectionTreeState.map(collection =>
                            <Collection
                                collection={collection}
                                key={collection.id}
                            />
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const mapStateToProps = ({ auth, collections }) => ({
    storeId: auth.storeId,
    collectionTree: collections.collectionTree,
    gettingCollectionTree: collections.gettingCollectionTree
});

const mapDispatchToProps = dispatch => bindActionCreators({ getCollectionTree }, dispatch);

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProductCollections);